class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.suggestionListIsOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    // Avoid document-level pointer handlers from interfering with text selection inside the input
    ["mousedown", "pointerdown", "touchstart"].forEach((type) => {
      this.input.addEventListener(
        type,
        (e) => {
          e.stopPropagation();
        },
        { passive: true }
      );
    });
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    document.addEventListener("pointerdown", (event) => {
      if (
        this.suggestionListIsOpen &&
        !event.target.closest("[data-predictive-search-suggestions]")
      ) {
        this.suggestionListClose();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      // Allow Shift+Arrow to create text selections
      if (event.shiftKey) return;
      // Only hijack arrows when the predictive dropdown is open (navigating options)
      if (!this.getAttribute("open")) return;
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    await this.getSuggestedTerms(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&section_id=predictive-search&resources[type]=product,page,article,collection&resources[limit_scope]=each&resources[options][fields]=body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        const error = new Error(response.status);
        this.close();
        throw error;
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search").innerHTML;
      // Save bandwidth keeping the cache in all instances synced
      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error?.code === 20) {
        // Code 20 means the call was aborted
        return;
      }
      this.close();
      throw error; // Preserve original error behavior
    }
  }

  renderSuggestedTerms(suggestions) {
    const suggestionsContainer = this.querySelector(
      "[data-predictive-search-suggestions]"
    );
    suggestionsContainer.innerHTML = "";

    suggestions.forEach((term) => {
      const suggestionItem = document.createElement("a");
      suggestionItem.classList.add(
        "predictive-search__item",
        "link",
        "link--text"
      );
      suggestionItem.innerHTML = `
      <div class="predictive-search__item-content predictive-search__item-content--centered">
        <p class="predictive-search__item-heading h5">teste</p>
      </div>`;
      suggestionItem.href = term.url;
      suggestionsContainer.appendChild(suggestionItem);
    });
    this.suggestionListOpen();
  }

  async getSuggestedTerms(searchTerm) {
    const response = await fetch(
      `${routes.predictive_search_url}.json?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=query&resources[options][fields]=body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor`,
      { signal: this.abortController.signal }
    );

    if (!response.ok) {
      const error = new Error(response.status);
      this.close();
      throw error;
    }

    const suggestedTerms = await response.json();
    this.renderSuggestedTerms(suggestedTerms);
  }

  renderSuggestedTerms(suggestedTerms) {
    const suggestionsContainer = this.querySelector(
      "[data-predictive-search-suggestions]"
    );
    suggestionsContainer.innerHTML = "";

    const queries = suggestedTerms?.resources?.results?.queries;

    if (!queries?.length) {
      this.suggestionListClose();
      return;
    }

    queries.forEach((term) => {
      const suggestionItem = document.createElement("a");
      suggestionItem.classList.add(
        "predictive-search__item",
        "link",
        "link--text"
      );
      suggestionItem.innerHTML = `
      <div class="predictive-search__item-content predictive-search__item-content--centered">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="13.5" y1="13.5" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <p class="predictive-search__item-heading h5">
        ${term.styled_text}
        </p>
      </div>`;
      suggestionItem.href = term.url;
      suggestionsContainer.appendChild(suggestionItem);
    });
    this.suggestionListOpen();
  }

  suggestionListOpen() {
    const field = this.querySelector(".field");
    field.classList.add("suggestions-is-opened");
    this.suggestionListIsOpen = true;
  }
  suggestionListClose() {
    const field = this.querySelector(".field");
    field.classList.remove("suggestions-is-opened");
    this.suggestionListIsOpen = false;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    const closestContentElement = this.closest(".search-modal__content");
    const closestSearchModaElement = this.closest(".search-modal");
    if (closestContentElement && closestContentElement.offsetHeight) {
      // If the closest content element has a height, use it
      this.resultsMaxHeight =
        closestContentElement.offsetHeight -
        closestSearchModaElement.offsetHeight;
      return this.resultsMaxHeight;
    }
    // fallback to 90vh if not found
    this.resultsMaxHeight = window.innerHeight * 0.9;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
